/**
 * Annotation Utility Functions
 * Helper functions for creating, validating, and repairing annotations
 */

import { Block, ContentAST, ListItem, findAnyById, getBlockText, getInlineText } from '../transforms/ast'
import {
  Annotation,
  AnnotationStatus,
  AnnotationWithStatus,
  BlockAnchor,
  BlockBoundarySnapshot,
  BlockSpanAnchor,
  InlineRangeAnchor,
  SelectionContext,
  isBlockAnchor,
  isBlockSpanAnchor,
  isInlineRangeAnchor,
} from '../types/annotations'

// --- ID Generation ---

export function generateAnnotationId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 7)
  return `ann_${timestamp}_${random}`
}

// --- Annotation Creation ---

/** Create a block annotation (tags a single block) */
export function createBlockAnnotation(blockId: string, tags: string[]): Annotation {
  const anchor: BlockAnchor = {
    type: 'block',
    blockId,
  }

  return {
    id: generateAnnotationId(),
    tags,
    anchor,
    createdAt: new Date(),
  }
}

function getBoundaryTextPreview(text: string, fromStart: boolean, maxLength: number = 32): string {
  if (text.length <= maxLength) {
    return text
  }

  return fromStart ? text.slice(0, maxLength) : text.slice(-maxLength)
}

function createBlockBoundarySnapshot(ast: ContentAST, blockId: string): BlockBoundarySnapshot | undefined {
  const blockIndex = ast.blocks.findIndex((block) => block.id === blockId)
  if (blockIndex === -1) {
    return undefined
  }

  const block = ast.blocks[blockIndex]
  const text = getBlockText(block)

  return {
    blockType: block.type,
    textPrefix: getBoundaryTextPreview(text, true),
    textSuffix: getBoundaryTextPreview(text, false),
    blockIndex,
    previousBlockId: ast.blocks[blockIndex - 1]?.id,
    nextBlockId: ast.blocks[blockIndex + 1]?.id,
  }
}

/** Create a block span annotation (tags multiple consecutive blocks) */
export function createSpanAnnotation(
  startBlockId: string,
  endBlockId: string,
  tags: string[],
  ast?: ContentAST,
): Annotation {
  const anchor: BlockSpanAnchor = {
    type: 'blockSpan',
    startBlockId,
    endBlockId,
    startSnapshot: ast ? createBlockBoundarySnapshot(ast, startBlockId) : undefined,
    endSnapshot: ast ? createBlockBoundarySnapshot(ast, endBlockId) : undefined,
  }

  return {
    id: generateAnnotationId(),
    tags,
    anchor,
    createdAt: new Date(),
  }
}

/** Create an inline range annotation with context for resilience */
export function createRangeAnnotation(
  startBlockId: string,
  startOffset: number,
  endBlockId: string,
  endOffset: number,
  tags: string[],
  ast: ContentAST,
): Annotation {
  // Extract context for fuzzy matching
  const context = extractRangeContext(ast, startBlockId, startOffset, endBlockId, endOffset)

  const anchor: InlineRangeAnchor = {
    type: 'range',
    startBlockId,
    startOffset,
    endBlockId,
    endOffset,
    textBefore: context.textBefore,
    taggedText: context.taggedText,
    textAfter: context.textAfter,
  }

  return {
    id: generateAnnotationId(),
    tags,
    anchor,
    createdAt: new Date(),
  }
}

/** Create an annotation from a selection context */
export function createAnnotationFromSelection(
  selection: SelectionContext,
  tags: string[],
  ast: ContentAST,
): Annotation {
  switch (selection.type) {
    case 'collapsed':
    case 'singleBlock':
      return createBlockAnnotation(selection.startBlockId, tags)

    case 'multiBlock':
      return createSpanAnnotation(selection.startBlockId, selection.endBlockId, tags, ast)

    case 'textRange':
      return createRangeAnnotation(
        selection.startBlockId,
        selection.startOffset ?? 0,
        selection.endBlockId,
        selection.endOffset ?? 0,
        tags,
        ast,
      )
  }
}

/** Get all blocks between two block IDs (inclusive) */
function getBlocksInRange(ast: ContentAST, startBlockId: string, endBlockId: string): Block[] {
  const blocks: Block[] = []
  let inRange = false

  for (const block of ast.blocks) {
    if (block.id === startBlockId) {
      inRange = true
    }

    if (inRange) {
      blocks.push(block)
    }

    if (block.id === endBlockId) {
      break
    }
  }

  return blocks
}

function getSelectableText(target: Block | ListItem): string {
  if ('type' in target) {
    return getBlockText(target)
  }

  let text = getInlineText(target.content)
  if (target.children && target.children.length > 0) {
    const childText = target.children.map((child) => getSelectableText(child)).join('\n')
    text += '\n' + childText
  }
  return text
}

/** Extract context around a text range for fuzzy matching */
function extractRangeContext(
  ast: ContentAST,
  startBlockId: string,
  startOffset: number,
  endBlockId: string,
  endOffset: number,
): { textBefore: string; taggedText: string; textAfter: string } {
  const CONTEXT_LENGTH = 20

  // Get all text from blocks in range
  const blocks = getBlocksInRange(ast, startBlockId, endBlockId)
  if (blocks.length === 0) {
    return { textBefore: '', taggedText: '', textAfter: '' }
  }

  // Single block case
  if (startBlockId === endBlockId) {
    const target = findAnyById(ast, startBlockId)
    const text = target ? getSelectableText(target) : getBlockText(blocks[0])
    const taggedText = text.slice(startOffset, endOffset)
    const textBefore = text.slice(Math.max(0, startOffset - CONTEXT_LENGTH), startOffset)
    const textAfter = text.slice(endOffset, endOffset + CONTEXT_LENGTH)

    return { textBefore, taggedText, textAfter }
  }

  // Multi-block case
  const firstBlockText = getBlockText(blocks[0])
  const lastBlockText = getBlockText(blocks[blocks.length - 1])

  // Text before starts in first block
  const textBefore = firstBlockText.slice(Math.max(0, startOffset - CONTEXT_LENGTH), startOffset)

  // Tagged text spans from first block's offset to last block's offset
  let taggedText = firstBlockText.slice(startOffset)
  for (let i = 1; i < blocks.length - 1; i++) {
    taggedText += '\n' + getBlockText(blocks[i])
  }
  if (blocks.length > 1) {
    taggedText += '\n' + lastBlockText.slice(0, endOffset)
  }

  // Text after ends in last block
  const textAfter = lastBlockText.slice(endOffset, endOffset + CONTEXT_LENGTH)

  return { textBefore, taggedText, textAfter }
}

// --- Validation ---

function blockExists(ast: ContentAST, blockId: string): boolean {
  return findAnyById(ast, blockId) !== null
}

function isRangeValid(
  ast: ContentAST,
  startBlockId: string,
  startOffset: number,
  endBlockId: string,
  endOffset: number,
): boolean {
  const startBlock = findAnyById(ast, startBlockId)
  const endBlock = findAnyById(ast, endBlockId)

  if (!startBlock || !endBlock) {
    return false
  }

  const startText = getSelectableText(startBlock)
  const endText = getSelectableText(endBlock)

  // Check offsets are within bounds
  if (startOffset < 0 || startOffset > startText.length) {
    return false
  }
  if (endOffset < 0 || endOffset > endText.length) {
    return false
  }

  return true
}

function getBlockIndex(ast: ContentAST, blockId: string): number {
  return ast.blocks.findIndex((block) => block.id === blockId)
}

function isSpanValid(ast: ContentAST, startBlockId: string, endBlockId: string): boolean {
  const startIndex = getBlockIndex(ast, startBlockId)
  const endIndex = getBlockIndex(ast, endBlockId)
  return startIndex !== -1 && endIndex !== -1 && startIndex <= endIndex
}

function scoreBoundaryCandidate(
  block: Block,
  candidateIndex: number,
  snapshot: BlockBoundarySnapshot,
  previousIndex: number,
  nextIndex: number,
): number {
  const text = getBlockText(block)
  let score = 0

  if (block.type === snapshot.blockType) {
    score += 4
  }

  if (snapshot.textPrefix.length > 0) {
    if (text.startsWith(snapshot.textPrefix)) {
      score += 5
    } else if (text.includes(snapshot.textPrefix)) {
      score += 2
    }
  }

  if (snapshot.textSuffix.length > 0) {
    if (text.endsWith(snapshot.textSuffix)) {
      score += 5
    } else if (text.includes(snapshot.textSuffix)) {
      score += 2
    }
  }

  if (snapshot.previousBlockId) {
    score += previousIndex !== -1 && candidateIndex > previousIndex ? 3 : -4
  }

  if (snapshot.nextBlockId) {
    score += nextIndex !== -1 && candidateIndex < nextIndex ? 3 : -4
  }

  const distance = Math.abs(candidateIndex - snapshot.blockIndex)
  score -= Math.min(distance, 6)

  return score
}

function blockMatchesBoundarySnapshot(
  block: Block | undefined,
  snapshot: BlockBoundarySnapshot,
  side: 'start' | 'end',
): boolean {
  if (!block || block.type !== snapshot.blockType) {
    return false
  }

  const text = getBlockText(block)
  if (side === 'start') {
    if (!snapshot.textPrefix) {
      return true
    }
    return text.startsWith(snapshot.textPrefix) || text.includes(snapshot.textPrefix)
  }

  if (!snapshot.textSuffix) {
    return true
  }
  return text.endsWith(snapshot.textSuffix) || text.includes(snapshot.textSuffix)
}

function combinedBlocksMatchBoundarySnapshot(
  primaryBlock: Block | undefined,
  adjacentBlock: Block | undefined,
  snapshot: BlockBoundarySnapshot,
  side: 'start' | 'end',
): boolean {
  if (!primaryBlock || !adjacentBlock) {
    return false
  }
  if (primaryBlock.type !== snapshot.blockType || adjacentBlock.type !== snapshot.blockType) {
    return false
  }

  const combinedText =
    side === 'start'
      ? `${getBlockText(adjacentBlock)}${getBlockText(primaryBlock)}`
      : `${getBlockText(primaryBlock)}${getBlockText(adjacentBlock)}`

  if (side === 'start') {
    if (!snapshot.textPrefix) {
      return true
    }
    return combinedText.startsWith(snapshot.textPrefix)
  }

  if (!snapshot.textSuffix) {
    return true
  }
  return combinedText.endsWith(snapshot.textSuffix)
}

function findBoundaryBlock(ast: ContentAST, snapshot?: BlockBoundarySnapshot): string | null {
  if (!snapshot || ast.blocks.length === 0) {
    return null
  }

  const previousIndex = snapshot.previousBlockId ? getBlockIndex(ast, snapshot.previousBlockId) : -1
  const nextIndex = snapshot.nextBlockId ? getBlockIndex(ast, snapshot.nextBlockId) : -1
  const estimatedIndex =
    previousIndex !== -1 && nextIndex !== -1
      ? Math.round((previousIndex + nextIndex) / 2)
      : previousIndex !== -1
        ? previousIndex + 1
        : nextIndex !== -1
          ? nextIndex - 1
          : snapshot.blockIndex

  const SEARCH_WINDOW = 6
  const windowStart = Math.max(0, previousIndex !== -1 ? previousIndex + 1 - 2 : estimatedIndex - SEARCH_WINDOW)
  const windowEnd = Math.min(
    ast.blocks.length - 1,
    nextIndex !== -1 ? nextIndex - 1 + 2 : estimatedIndex + SEARCH_WINDOW,
  )

  let bestMatch: { blockId: string; score: number } | null = null
  for (let i = windowStart; i <= windowEnd; i++) {
    const candidate = ast.blocks[i]
    const score = scoreBoundaryCandidate(candidate, i, snapshot, previousIndex, nextIndex)
    if (score < 3) {
      continue
    }

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { blockId: candidate.id, score }
    }
  }

  return bestMatch?.blockId ?? null
}

function repairSpanBoundary(
  ast: ContentAST,
  existingBlockId: string,
  snapshot: BlockBoundarySnapshot | undefined,
  side: 'start' | 'end',
): string | null {
  if (blockExists(ast, existingBlockId)) {
    if (!snapshot) {
      return existingBlockId
    }

    const existingIndex = getBlockIndex(ast, existingBlockId)
    const existingBlock = existingIndex !== -1 ? ast.blocks[existingIndex] : undefined
    const adjacentBlock =
      existingIndex !== -1 ? ast.blocks[side === 'start' ? existingIndex - 1 : existingIndex + 1] : undefined

    const existingMatches = blockMatchesBoundarySnapshot(existingBlock, snapshot, side)
    const adjacentMatches = blockMatchesBoundarySnapshot(adjacentBlock, snapshot, side)
    const combinedMatches = combinedBlocksMatchBoundarySnapshot(existingBlock, adjacentBlock, snapshot, side)

    if (combinedMatches) {
      return adjacentBlock?.id ?? existingBlockId
    }

    if (adjacentMatches && !existingMatches) {
      return adjacentBlock?.id ?? existingBlockId
    }

    return existingBlockId
  }

  return findBoundaryBlock(ast, snapshot)
}

function needsSpanRepair(ast: ContentAST, anchor: BlockSpanAnchor): boolean {
  if (!isSpanValid(ast, anchor.startBlockId, anchor.endBlockId)) {
    return true
  }

  if (!anchor.startSnapshot || !anchor.endSnapshot) {
    return true
  }

  const resolvedStart = repairSpanBoundary(ast, anchor.startBlockId, anchor.startSnapshot, 'start')
  const resolvedEnd = repairSpanBoundary(ast, anchor.endBlockId, anchor.endSnapshot, 'end')

  return resolvedStart !== anchor.startBlockId || resolvedEnd !== anchor.endBlockId
}

export function validateAnnotation(annotation: Annotation, ast: ContentAST): AnnotationStatus {
  const { anchor } = annotation

  if (isBlockAnchor(anchor)) {
    return blockExists(ast, anchor.blockId) ? 'valid' : 'orphaned'
  }

  if (isBlockSpanAnchor(anchor)) {
    if (isSpanValid(ast, anchor.startBlockId, anchor.endBlockId)) {
      return needsSpanRepair(ast, anchor) ? 'repaired' : 'valid'
    }

    return anchor.startSnapshot || anchor.endSnapshot ? 'repaired' : 'orphaned'
  }

  if (isInlineRangeAnchor(anchor)) {
    if (isRangeValid(ast, anchor.startBlockId, anchor.startOffset, anchor.endBlockId, anchor.endOffset)) {
      // Additionally check if the tagged text matches
      const context = extractRangeContext(
        ast,
        anchor.startBlockId,
        anchor.startOffset,
        anchor.endBlockId,
        anchor.endOffset,
      )

      if (context.taggedText === anchor.taggedText) {
        return 'valid'
      }

      // Text changed but range is technically valid - may need repair
      return 'repaired'
    }

    return 'orphaned'
  }

  return 'orphaned'
}

export function validateAnnotations(annotations: Annotation[], ast: ContentAST): AnnotationWithStatus[] {
  return annotations.map((annotation) => ({
    ...annotation,
    status: validateAnnotation(annotation, ast),
  }))
}

// --- Repair ---

/** Find text in AST and return its location */
function findTextInAst(
  ast: ContentAST,
  targetText: string,
  textBefore: string,
  textAfter: string,
  nearBlockId?: string,
): { blockId: string; offset: number } | null {
  // First, try to find in the vicinity of nearBlockId (falls back to 0 when the block no longer exists)
  const startSearchIndex = Math.max(0, nearBlockId ? ast.blocks.findIndex((b) => b.id === nearBlockId) : 0)

  // Search in a window around the original location
  const searchOrder: number[] = []
  for (let i = 0; i <= ast.blocks.length; i++) {
    if (startSearchIndex + i < ast.blocks.length) {
      searchOrder.push(startSearchIndex + i)
    }
    if (i > 0 && startSearchIndex - i >= 0) {
      searchOrder.push(startSearchIndex - i)
    }
  }

  for (const idx of searchOrder) {
    const block = ast.blocks[idx]
    const text = getBlockText(block)

    // Try to find the tagged text
    let searchPos = 0
    while (searchPos < text.length) {
      const foundPos = text.indexOf(targetText, searchPos)
      if (foundPos === -1) {
        break
      }

      // Verify with context
      const beforeMatch = text.slice(Math.max(0, foundPos - textBefore.length), foundPos)
      const afterMatch = text.slice(foundPos + targetText.length, foundPos + targetText.length + textAfter.length)

      // Score the match based on context similarity
      const beforeScore = textBefore.length > 0 ? (beforeMatch.endsWith(textBefore) ? 1 : 0) : 1
      const afterScore = textAfter.length > 0 ? (afterMatch.startsWith(textAfter) ? 1 : 0) : 1

      if (beforeScore + afterScore >= 1) {
        return { blockId: block.id, offset: foundPos }
      }

      searchPos = foundPos + 1
    }
  }

  return null
}

/** Attempt to repair an inline range annotation */
export function repairRangeAnnotation(annotation: Annotation, ast: ContentAST): Annotation | null {
  const { anchor } = annotation

  if (!isInlineRangeAnchor(anchor)) {
    return null
  }

  // Try to find the tagged text in the AST
  const found = findTextInAst(ast, anchor.taggedText, anchor.textBefore, anchor.textAfter, anchor.startBlockId)

  if (!found) {
    return null
  }

  // Create repaired anchor
  const repairedAnchor: InlineRangeAnchor = {
    type: 'range',
    startBlockId: found.blockId,
    startOffset: found.offset,
    endBlockId: found.blockId, // Assuming single block for now
    endOffset: found.offset + anchor.taggedText.length,
    textBefore: anchor.textBefore,
    taggedText: anchor.taggedText,
    textAfter: anchor.textAfter,
  }

  return {
    ...annotation,
    anchor: repairedAnchor,
  }
}

/** Attempt to repair a block span annotation */
export function repairSpanAnnotation(annotation: Annotation, ast: ContentAST): Annotation | null {
  const { anchor } = annotation

  if (!isBlockSpanAnchor(anchor)) {
    return null
  }

  let startBlockId = repairSpanBoundary(ast, anchor.startBlockId, anchor.startSnapshot, 'start')
  let endBlockId = repairSpanBoundary(ast, anchor.endBlockId, anchor.endSnapshot, 'end')

  if (!startBlockId && !endBlockId) {
    return null
  }

  if (!startBlockId) {
    startBlockId = endBlockId
  }
  if (!endBlockId) {
    endBlockId = startBlockId
  }
  if (!startBlockId || !endBlockId) {
    return null
  }

  let startIndex = getBlockIndex(ast, startBlockId)
  let endIndex = getBlockIndex(ast, endBlockId)
  if (startIndex === -1 || endIndex === -1) {
    return null
  }

  if (startIndex > endIndex) {
    if (!blockExists(ast, anchor.startBlockId) && blockExists(ast, anchor.endBlockId)) {
      startBlockId = endBlockId
      startIndex = endIndex
    } else if (!blockExists(ast, anchor.endBlockId) && blockExists(ast, anchor.startBlockId)) {
      endBlockId = startBlockId
      endIndex = startIndex
    } else {
      return null
    }
  }

  const repairedAnchor: BlockSpanAnchor = {
    type: 'blockSpan',
    startBlockId,
    endBlockId,
    startSnapshot: createBlockBoundarySnapshot(ast, startBlockId),
    endSnapshot: createBlockBoundarySnapshot(ast, endBlockId),
  }

  return {
    ...annotation,
    anchor: repairedAnchor,
  }
}

/**
 * How long an annotation whose anchor went dead is kept before it is dropped
 * for good. Mirrors the server's cut-paste grace window for ChapterSections
 * (ORPHAN_GRACE_MS in orphanChapterSectionCleanup.ts).
 */
export const ANNOTATION_ORPHAN_GRACE_MS = 5 * 60 * 1000

function withoutOrphanStamp(annotation: Annotation): Annotation {
  if (!annotation.orphanedAt) {
    return annotation
  }
  const next = { ...annotation }
  delete next.orphanedAt
  return next
}

/** Would persisting `after` in place of `before` actually write anything new? */
function annotationDiffers(before: Annotation, after: Annotation): boolean {
  return before.orphanedAt !== after.orphanedAt || JSON.stringify(before.anchor) !== JSON.stringify(after.anchor)
}

/**
 * Re-anchor every annotation against the current AST.
 *
 * An annotation whose anchor can't be resolved is **not** dropped: a cut and a
 * deletion are indistinguishable here, and a cut-paste reinstates the original
 * block IDs, so dropping would destroy the author's tags on an ordinary move.
 * It is stamped with `orphanedAt` and kept; the stamp is cleared the moment the
 * anchor resolves again (paste), and only an anchor that stayed dead past the
 * grace window is expired.
 */
export function repairAnnotations(
  annotations: AnnotationWithStatus[],
  ast: ContentAST,
  now: number = Date.now(),
): { repaired: Annotation[]; expired: Annotation[]; changed: boolean } {
  const repaired: Annotation[] = []
  const expired: Annotation[] = []
  let changed = false

  const keep = (original: Annotation, next: Annotation): void => {
    if (annotationDiffers(original, next)) {
      changed = true
    }
    repaired.push(next)
  }

  for (const annotation of annotations) {
    if (annotation.status === 'valid') {
      keep(annotation, withoutOrphanStamp(annotation))
      continue
    }

    // A single corrupt anchor must orphan that annotation, not crash the editor
    let fixed: Annotation | null = null
    try {
      if (isInlineRangeAnchor(annotation.anchor)) {
        fixed = repairRangeAnnotation(annotation, ast)
      } else if (isBlockSpanAnchor(annotation.anchor)) {
        fixed = repairSpanAnnotation(annotation, ast)
      }
    } catch {
      fixed = null
    }

    if (fixed) {
      keep(annotation, withoutOrphanStamp(fixed))
      continue
    }

    const orphanedSince = annotation.orphanedAt ? Date.parse(annotation.orphanedAt) : NaN
    if (Number.isNaN(orphanedSince)) {
      keep(annotation, { ...annotation, orphanedAt: new Date(now).toISOString() })
      continue
    }

    if (now - orphanedSince > ANNOTATION_ORPHAN_GRACE_MS) {
      expired.push(annotation)
      changed = true
      continue
    }

    repaired.push(annotation)
  }

  return { repaired, expired, changed }
}

// --- Query Helpers ---

/** Get all annotations that include a specific block */
export function getAnnotationsForBlock(annotations: Annotation[], blockId: string, ast: ContentAST): Annotation[] {
  return annotations.filter((annotation) => {
    const { anchor } = annotation

    if (isBlockAnchor(anchor)) {
      return anchor.blockId === blockId
    }

    if (isBlockSpanAnchor(anchor)) {
      // Check if blockId is between start and end
      const startIndex = ast.blocks.findIndex((b) => b.id === anchor.startBlockId)
      const endIndex = ast.blocks.findIndex((b) => b.id === anchor.endBlockId)
      const blockIndex = ast.blocks.findIndex((b) => b.id === blockId)

      return blockIndex >= startIndex && blockIndex <= endIndex
    }

    if (isInlineRangeAnchor(anchor)) {
      // Check if blockId is in the range
      if (anchor.startBlockId === anchor.endBlockId) {
        return anchor.startBlockId === blockId
      }

      const startIndex = ast.blocks.findIndex((b) => b.id === anchor.startBlockId)
      const endIndex = ast.blocks.findIndex((b) => b.id === anchor.endBlockId)
      const blockIndex = ast.blocks.findIndex((b) => b.id === blockId)

      return blockIndex >= startIndex && blockIndex <= endIndex
    }

    return false
  })
}

/** Get tag IDs for a specific block (from all annotation types) */
export function getTagsForBlock(annotations: Annotation[], blockId: string, ast: ContentAST): string[] {
  const relevantAnnotations = getAnnotationsForBlock(annotations, blockId, ast)
  const tagSet = new Set<string>()

  for (const annotation of relevantAnnotations) {
    for (const tagId of annotation.tags) {
      tagSet.add(tagId)
    }
  }

  return Array.from(tagSet)
}

/** Get block IDs that are annotated. excludeInlineOnly: excludes blocks with ONLY inline annotations */
export function getAnnotatedBlockIds(
  annotations: Annotation[],
  ast: ContentAST,
  excludeInlineOnly: boolean = false,
): Set<string> {
  const blockIds = new Set<string>()
  const inlineOnlyBlockIds = new Set<string>()

  for (const annotation of annotations) {
    const { anchor } = annotation

    if (isBlockAnchor(anchor)) {
      blockIds.add(anchor.blockId)
    } else if (isBlockSpanAnchor(anchor)) {
      const startIndex = ast.blocks.findIndex((b) => b.id === anchor.startBlockId)
      const endIndex = ast.blocks.findIndex((b) => b.id === anchor.endBlockId)

      for (let i = startIndex; i <= endIndex && i < ast.blocks.length; i++) {
        if (i >= 0) {
          blockIds.add(ast.blocks[i].id)
        }
      }
    } else if (isInlineRangeAnchor(anchor)) {
      // Track inline blocks separately
      if (anchor.startBlockId === anchor.endBlockId) {
        inlineOnlyBlockIds.add(anchor.startBlockId)
      } else {
        const startIndex = ast.blocks.findIndex((b) => b.id === anchor.startBlockId)
        const endIndex = ast.blocks.findIndex((b) => b.id === anchor.endBlockId)

        for (let i = startIndex; i <= endIndex && i < ast.blocks.length; i++) {
          if (i >= 0) {
            inlineOnlyBlockIds.add(ast.blocks[i].id)
          }
        }
      }
    }
  }

  // If not excluding inline-only, merge them in
  if (!excludeInlineOnly) {
    for (const id of inlineOnlyBlockIds) {
      blockIds.add(id)
    }
  }
  // else: inline-only blocks are already excluded (not in blockIds)

  return blockIds
}
